/*
 * Copyright (C) 2016 Bilibili. All Rights Reserved.
 *
 * @author zheng qian <xqq@xqq.im>
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import EventEmitter from 'eventemitter3';
import {logger} from '../../utils/logger.js';

import {IllegalStateException} from '../../utils/exception.js';

// Media Source Extensions controller
class MSEController extends EventEmitter {

    player;

    tag = 'MSEController'

    constructor(player) {
   
        super()

        this.player = player;

        // if (this.config.isLive && this.config.autoCleanupSourceBuffer == undefined) {
        //     // For live stream, do auto cleanup by default
        //     this.config.autoCleanupSourceBuffer = true;
        // }

        this.e = {
            onSourceOpen: this.onSourceOpen.bind(this),
            onSourceEnded: this.onSourceEnded.bind(this),
            onSourceClose: this.onSourceClose.bind(this),
            onSourceBufferError: this.onSourceBufferError.bind(this),
            onSourceBufferUpdateEnd: this.onSourceBufferUpdateEnd.bind(this)
        };

        this.mediaSource = null;
        this.mediaSourceObjectURL = null;

        this.isBufferFull = false;
        this.hasPendingEos = false;

        this.requireSetMediaDuration = false;
        this.pendingMediaDuration = 0;

        this.pendingSourceBufferInit = [];
        this.mimeTypes = {
            video: null,
            audio: null
        };
        this.sourceBuffers = {
            video: null,
            audio: null
        };
        this.lastInitSegments = {
            video: null,
            audio: null
        };
        this.pendingSegments = {
            video: [],
            audio: []
        };
        this.pendingRemoveRanges = {
            video: [],
            audio: []
        };

        let ms =  new self.MediaSource();
        ms.addEventListener('sourceopen', this.e.onSourceOpen);
        ms.addEventListener('sourceended', this.e.onSourceEnded);
        ms.addEventListener('sourceclose', this.e.onSourceClose);

        this.mediaSource = ms
        
        this.isDestroyed = false
    }

    destroy() {
        if (this.mediaSource) {
            this.shutdown();
        }
        if (this.mediaSourceObjectURL) {
            this.revokeObjectURL();
        }
        this.e = null;
        this.isDestroyed = true
        this.off()

        logger.info(this.tag, 'MSE-Controller destroy');
    }


    shutdown() {
        if (this.mediaSource) {
            let ms = this.mediaSource;
            for (let type in this.sourceBuffers) {
                // pending segments should be discard
                let ps = this.pendingSegments[type];
                ps.splice(0, ps.length);
                this.pendingSegments[type] = null;
                this.pendingRemoveRanges[type] = null;
                this.lastInitSegments[type] = null;

                // remove all sourcebuffers
                let sb = this.sourceBuffers[type];
                if (sb) {
                    if (ms.readyState !== 'closed') {
                        // ms edge can throw an error: Unexpected call to method or property access
                        try {
                            ms.removeSourceBuffer(sb);
                        } catch (error) {
                            logger.error(this.tag, error.message);
                        }
                        sb.removeEventListener('error', this.e.onSourceBufferError);
                        sb.removeEventListener('updateend', this.e.onSourceBufferUpdateEnd);
                    }
                    this.mimeTypes[type] = null;
                    this.sourceBuffers[type] = null;
                }
            }
            if (ms.readyState === 'open') {
                try {
                    ms.endOfStream();
                } catch (error) {
                    logger.error(this.tag, error.message);
                }
            }
            this.mediaElementProxy = null;
            ms.removeEventListener('sourceopen', this.e.onSourceOpen);
            ms.removeEventListener('sourceended', this.e.onSourceEnded);
            ms.removeEventListener('sourceclose', this.e.onSourceClose);

            this.pendingSourceBufferInit = [];
            this.isBufferFull = false;
            this.mediaSource = null;
        }
    }


    getObject() {
        if (!this.mediaSource) {
            throw new IllegalStateException('MediaSource has not been initialized yet!');
        }
        return this.mediaSource;
    }

    getHandle() {
        if (!this.mediaSource) {
            throw new IllegalStateException('MediaSource has not been initialized yet!');
        }
        return this.mediaSource.handle;
    }

    getObjectURL() {
        if (!this.mediaSource) {
            throw new IllegalStateException('MediaSource has not been initialized yet!');
        }

        if (this.mediaSourceObjectURL == null) {
            this.mediaSourceObjectURL = URL.createObjectURL(this.mediaSource);
        }
        return this.mediaSourceObjectURL;
    }

    revokeObjectURL() {
        if (this.mediaSourceObjectURL) {
            URL.revokeObjectURL(this.mediaSourceObjectURL);
            this.mediaSourceObjectURL = null;
        }
    }

    appendInitSegment(initSegment, deferred = undefined) {

        if (this.isDestroyed) {

            return
        }

        if (!this.mediaSource || this.mediaSource.readyState !== 'open') {
            // sourcebuffer creation requires mediaSource.readyState === 'open'
            // so we defer the sourcebuffer creation, until sourceopen event triggered
            this.pendingSourceBufferInit.push(initSegment);
            // make sure that this InitSegment is in the front of pending segments queue
            this.pendingSegments[initSegment.type].push(initSegment);
            return;
        }

        let is = initSegment;
        let mimeType = `${is.container}`;
        if (is.codec && is.codec.length > 0) {
            mimeType += `;codecs=${is.codec}`;
        }

        let firstInitSegment = false;

        logger.info(this.tag, 'Received Initialization Segment, mimeType: ' + mimeType);
        this.lastInitSegments[is.type] = is;

        if (mimeType !== this.mimeTypes[is.type]) {
            if (!this.mimeTypes[is.type]) {  // empty, first chance create sourcebuffer
                firstInitSegment = true;
                try {
                    let sb = this.sourceBuffers[is.type] = this.mediaSource.addSourceBuffer(mimeType);
                    sb.addEventListener('error', this.e.onSourceBufferError);
                    sb.addEventListener('updateend', this.e.onSourceBufferUpdateEnd);
                } catch (error) {
                    logger.error(this.tag, error.message);
                    //this.emit(MSEEvents.ERROR, {code: error.code, msg: error.message});
                    this.emit('playAbnormal', {message:error.message})
                    return;
                }
            } else {
                logger.info(this.tag, `Notice: ${is.type} mimeType changed, origin: ${this.mimeTypes[is.type]}, target: ${mimeType}`);
            }
            this.mimeTypes[is.type] = mimeType;
        }

        if (!deferred) {
            // deferred means this InitSegment has been pushed to pendingSegments queue
            this.pendingSegments[is.type].push(is);
        }

        if (!firstInitSegment) {  // append immediately only if init segment in subsequence
            if (this.sourceBuffers[is.type] && !this.sourceBuffers[is.type].updating) {
                this.doAppendSegments();
            }
        }
        // if (Browser.safari && is.container === 'audio/mpeg' && is.mediaDuration > 0) {
        //     // 'audio/mpeg' track under Safari may cause MediaElement's duration to be NaN
        //     // Manually correct MediaSource.duration to make progress bar seekable, and report right duration
        //     this.requireSetMediaDuration = true;
        //     this.pendingMediaDuration = is.mediaDuration / 1000;  // in seconds
        //     this.updateMediaSourceDuration();
        // }
    }

    appendMediaSegment(mediaSegment) {

        if (this.isDestroyed) {

            return
        }
        
        let ms = mediaSegment;
        this.pendingSegments[ms.type].push(ms);

        // if (this.config.autoCleanupSourceBuffer && this.needCleanupSourceBuffer()) {
        //     this.doCleanupSourceBuffer();
        // }

        let sb = this.sourceBuffers[ms.type];
        if (sb && !sb.updating && !this.hasPendingRemoveRanges()) {
            this.doAppendSegments();
        }
    }

    flush() {
        // remove all appended buffers
        for (let type in this.sourceBuffers) {
            if (!this.sourceBuffers[type]) {
                continue;
            }

            // abort current buffer append algorithm
            let sb = this.sourceBuffers[type];
            if (this.mediaSource.readyState === 'open') {
                try {
                    // If range removal algorithm is running, InvalidStateError will be throwed
                    // Ignore it.
                    sb.abort();
                } catch (error) {
                    logger.error(this.tag, error.message);
                }
            }

            // pending segments should be discard
            let ps = this.pendingSegments[type];
            ps.splice(0, ps.length);

            if (this.mediaSource.readyState === 'closed') {
                // Parent MediaSource object has been detached from HTMLMediaElement
                continue;
            }

            // record ranges to be remove from SourceBuffer
            for (let i = 0; i < sb.buffered.length; i++) {
                let start = sb.buffered.start(i);
                let end = sb.buffered.end(i);
                this.pendingRemoveRanges[type].push({start, end});
            }

            // if sb is not updating, let's remove ranges now!
            if (!sb.updating) {
                this.doRemoveRanges();
            }
            // Safari 10 may get InvalidStateError in the later appendBuffer() after SourceBuffer.remove() call
            // Internal parser's state may be invalid at this time. Re-append last InitSegment to workaround.
            // Related issue: https://bugs.webkit.org/show_bug.cgi?id=159230
            if (Browser.safari) {
                let lastInitSegment = this.lastInitSegments[type];
                if (lastInitSegment) {
                    this.pendingSegments[type].push(lastInitSegment);
                    if (!sb.updating) {
                        this.doAppendSegments();
                    }
                }
            }
        }
    }

    endOfStream() {
        let ms = this.mediaSource;
        let sb = this.sourceBuffers;
        if (!ms || ms.readyState !== 'open') {
            if (ms && ms.readyState === 'closed' && this.hasPendingSegments()) {
                // If MediaSource hasn't turned into open state, and there're pending segments
                // Mark pending endOfStream, defer call until all pending segments appended complete
                this.hasPendingEos = true;
            }
            return;
        }
        if (sb.video && sb.video.updating || sb.audio && sb.audio.updating) {
            // If any sourcebuffer is updating, defer endOfStream operation
            // See _onSourceBufferUpdateEnd()
            this.hasPendingEos = true;
        } else {
            this.hasPendingEos = false;
            // Notify media data loading complete
            // This is helpful for correcting total duration to match last media segment
            // Otherwise MediaElement's ended event may not be triggered
            ms.endOfStream();
        }
    }

    needCleanupSourceBuffer() {
        if (!this.config.autoCleanupSourceBuffer) {
            return false;
        }

        let currentTime = this.mediaElementProxy.getCurrentTime();

        for (let type in this.sourceBuffers) {
            let sb = this.sourceBuffers[type];
            if (sb) {
                let buffered = sb.buffered;
                if (buffered.length >= 1) {
                   
                    let start = buffered.start(0) 
                    let end  = buffered.end(0) 
    
                    if (currentTime - start >= this.config.autoCleanupMaxBackwardDuration) {

                        console.log(`${start} ${end}`)
                        return true;
                    }
                }
            }
        }

        return false;
    }

    doCleanupSourceBuffer() {
        let currentTime = this.mediaElementProxy.getCurrentTime();

        for (let type in this.sourceBuffers) {
            let sb = this.sourceBuffers[type];
            if (sb) {
                let buffered = sb.buffered;
                let doRemove = false;

                for (let i = 0; i < buffered.length; i++) {
                    let start = buffered.start(i);
                    let end = buffered.end(i);

                    if (start <= currentTime && currentTime < end + 3) {  // padding 3 seconds
                        if (currentTime - start >= this.config.autoCleanupMaxBackwardDuration) {
                            doRemove = true;
                            let removeEnd = currentTime - this.config.autoCleanupMinBackwardDuration;
                            this.pendingRemoveRanges[type].push({start: start, end: removeEnd});
                        }
                    } else if (end < currentTime) {
                        doRemove = true;
                        this.pendingRemoveRanges[type].push({start: start, end: end});
                    }
                }

                if (doRemove && !sb.updating) {
                    this.doRemoveRanges();
                }
            }
        }
    }

    updateMediaSourceDuration() {
        let sb = this.sourceBuffers;
        if (this.mediaElementProxy.getReadyState() === 0 || this.mediaSource.readyState !== 'open') {
            return;
        }
        if ((sb.video && sb.video.updating) || (sb.audio && sb.audio.updating)) {
            return;
        }

        let current = this.mediaSource.duration;
        let target = this.pendingMediaDuration;

        if (target > 0 && (isNaN(current) || target > current)) {
            Log.v(this.TAG, `Update MediaSource duration from ${current} to ${target}`);
            this.mediaSource.duration = target;
        }

        this.requireSetMediaDuration = false;
        this.pendingMediaDuration = 0;
    }

    doRemoveRanges() {
        for (let type in this.pendingRemoveRanges) {
            if (!this.sourceBuffers[type] || this.sourceBuffers[type].updating) {
                continue;
            }
            let sb = this.sourceBuffers[type];
            let ranges = this.pendingRemoveRanges[type];
            while (ranges.length && !sb.updating) {
                let range = ranges.shift();
                sb.remove(range.start, range.end);
            }
        }
    }

    doAppendSegments() {

        if (this.isDestroyed) {

            return
        }
        let pendingSegments = this.pendingSegments;

        for (let type in pendingSegments) {
            if (!this.sourceBuffers[type] || this.sourceBuffers[type].updating) {
                continue;
            }

            if (pendingSegments[type].length > 0) {
                let segment = pendingSegments[type].shift();

                // if (segment.timestampOffset) {
                //     // For MPEG audio stream in MSE, if unbuffered-seeking occurred
                //     // We need explicitly set timestampOffset to the desired point in timeline for mpeg SourceBuffer.
                //     let currentOffset = this.sourceBuffers[type].timestampOffset;
                //     let targetOffset = segment.timestampOffset / 1000;  // in seconds

                //     let delta = Math.abs(currentOffset - targetOffset);
                //     if (delta > 0.1) {  // If time delta > 100ms
                //         Log.v(this.TAG, `Update MPEG audio timestampOffset from ${currentOffset} to ${targetOffset}`);
                //         this.sourceBuffers[type].timestampOffset = targetOffset;
                //     }
                //     delete segment.timestampOffset;
                // }

                if (!segment.data || segment.data.byteLength === 0) {
                    // Ignore empty buffer
                    continue;
                }

                try {
                    this.sourceBuffers[type].appendBuffer(segment.data);
                    this.isBufferFull = false;
                } catch (error) {
                    this.pendingSegments[type].unshift(segment);
                    if (error.code === 22) {  // QuotaExceededError
                        /* Notice that FireFox may not throw QuotaExceededError if SourceBuffer is full
                         * Currently we can only do lazy-load to avoid SourceBuffer become scattered.
                         * SourceBuffer eviction policy may be changed in future version of FireFox.
                         *
                         * Related issues:
                         * https://bugzilla.mozilla.org/show_bug.cgi?id=1279885
                         * https://bugzilla.mozilla.org/show_bug.cgi?id=1280023
                         */

                        // report buffer full, abort network IO
                        if (!this.isBufferFull) {
                            logger.warn(this.tag, `buffer is full`);
                           // this.emit(MSEEvents.BUFFER_FULL);
                        }
                        this.isBufferFull = true;
                    } else {
                        logger.error(this.tag, error.message);
                        //this.emit(MSEEvents.ERROR, {code: error.code, msg: error.message});
                        this.emit('playAbnormal', {message:error.message})
                    }
                }
            }
        }
    }

    onSourceOpen() {

        logger.info(this.tag, 'MediaSource onSourceOpen');
        this.mediaSource.removeEventListener('sourceopen', this.e.onSourceOpen);
        // deferred sourcebuffer creation / initialization
        if (this.pendingSourceBufferInit.length > 0) {
            let pendings = this.pendingSourceBufferInit;
            while (pendings.length) {
                let segment = pendings.shift();
                this.appendInitSegment(segment, true);
            }
        }
        // there may be some pending media segments, append them
        if (this.hasPendingSegments()) {
            this.doAppendSegments();
        }
       // this.emit(MSEEvents.SOURCE_OPEN);
    }


    onSourceEnded() {
        // fired on endOfStream
        logger.info(this.tag, 'MediaSource onSourceEnded');
    }

    onSourceClose() {
        // fired on detaching from media element
        logger.info(this.tag, 'MediaSource onSourceClose');

        if (this.mediaSource && this.e != null) {
            this.mediaSource.removeEventListener('sourceopen', this.e.onSourceOpen);
            this.mediaSource.removeEventListener('sourceended', this.e.onSourceEnded);
            this.mediaSource.removeEventListener('sourceclose', this.e.onSourceClose);
        }
    }

    hasPendingSegments() {
        let ps = this.pendingSegments;
        return ps.video.length > 0 || ps.audio.length > 0;
    }
    
    hasPendingRemoveRanges() {
        let prr = this.pendingRemoveRanges;
        return prr.video.length > 0 || prr.audio.length > 0;
    }

 
    onSourceBufferUpdateEnd() {

        if (this.isDestroyed) {

            return
        }

      if (this.requireSetMediaDuration) {
            this.updateMediaSourceDuration();
        } else if (this.hasPendingRemoveRanges()) {
            this.doRemoveRanges();
        } else  if (this.hasPendingSegments()) {
            this.doAppendSegments();
        } else if (this.hasPendingEos) {
            this.endOfStream();
        }
      //  this.emit(MSEEvents.UPDATE_END);
    }

    onSourceBufferError(e) {
        logger.error(this.tag, `SourceBuffer Error: ${JSON.stringify(e)}`);
        // this error might not always be fatal, just ignore it
    }

}

export default MSEController;